import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { showError, showSuccess } from "@/utils/toast";

interface Version {
  id: number;
  name: string;
}

interface NewVersionDialogProps {
  gameId: number;
  versions: Version[];
  onVersionAdded: (newVersion: Version) => void;
}

export const NewVersionDialog = ({ gameId, versions, onVersionAdded }: NewVersionDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sourceVersion, setSourceVersion] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getCsrfToken = (): string | null => {
    const tokenEl = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
    return tokenEl ? tokenEl.content : null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;
    setIsSubmitting(true);

    const csrfToken = getCsrfToken();
    if (!csrfToken) {
      showError("Biztonsági hiba: A CSRF token hiányzik.");
      setIsSubmitting(false);
      return;
    }

    const body = JSON.stringify({
      version: {
        name: name,
        description: description,
        source_version_id: sourceVersion ? parseInt(sourceVersion, 10) : null,
      },
    });

    try {
      const response = await fetch(`/api/games/${gameId}/versions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        body,
      });
      if (!response.ok) throw new Error("A verzió hozzáadása sikertelen volt.");
      const newVersion = await response.json();
      showSuccess(`'${name}' verzió sikeresen hozzáadva!`);
      onVersionAdded(newVersion);
      setIsOpen(false);
      setName("");
      setDescription("");
      setSourceVersion(undefined);
    } catch (error) {
      showError(error instanceof Error ? error.message : "Ismeretlen hiba történt.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>Új verzió felvétele</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Új verzió felvétele</DialogTitle>
          <DialogDescription>
            Add meg az új verzió számát. A meglévő fordítások átmásolásához válassz egy származtatási verziót.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="version-name" className="text-right">
                Verziószám
              </Label>
              <Input
                id="version-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="col-span-3"
                placeholder="Pl. 1.6.1"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Leírás
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="col-span-3"
                placeholder="Pl. A játék mostantól támogatja a magyar nyelvet."
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="source-version" className="text-right">
                Származtatás
              </Label>
              <Select onValueChange={setSourceVersion} value={sourceVersion}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Válassz verziót..." />
                </SelectTrigger>
                <SelectContent>
                  {versions.map((version) => (
                    <SelectItem key={version.id} value={String(version.id)}>
                      {version.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isSubmitting || !name}>
              {isSubmitting ? "Hozzáadás..." : "Verzió hozzáadása"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};